import { Download, FileText, ExternalLink, ChevronDown } from "lucide-react";
import { Card, PageHeader } from "../fundercomponents/funderComponents";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useFunderReportsHook } from "../../hooks/useFunderHooks";
import "./reports.css";

export default function Reports() {
  const { user, token } = useAuth();
  const isFunder = user?.role === ROLES.FUNDER;

  const { data: reportsApiData, isLoading } = useFunderReportsHook({
    token,
    userId: user?.id,
    enabled: isFunder,
  });

  const reportsData = reportsApiData ?? {
    downloadable_reports: [],
    methodology_notes: [],
    verified_success_stories: [],
  };

  if (isLoading && isFunder) {
    return (
      <div className="funderReports">
        <PageHeader
          title="Reports & Documentation"
          description="Downloadable reports, methodology, and success stories."
        />
        <p className="admin-muted">Loading reports data...</p>
      </div>
    );
  }

  return (
    <div className="funderReports">
      <PageHeader
        title="Reports & Documentation"
        description="Downloadable reports, methodology, and success stories."
      />

      <Card
        title="Downloadable Reports"
        description="Export portfolio data and impact summaries."
      >
        <div className="funderReports-reportGrid">
          {(reportsData.downloadable_reports ?? []).map(
            (report, reportIndex) => (
              <div
                key={`${report.name}-${reportIndex}`}
                className="funderReports-reportCard"
              >
                <div className="funderReports-reportTitle">
                  <FileText
                    className="funderReports-icon-md funderReports-icon-primary"
                    aria-hidden="true"
                  />
                  <span>{report.name}</span>
                </div>
                <p className="funderReports-reportDesc">{report.description}</p>
                <div className="funderReports-reportActions">
                  {(report.formats ?? []).map((format, formatIndex) => (
                    <DownloadButton
                      key={`${format.format_type}-${formatIndex}`}
                      format={format.format_type}
                      link={format.link}
                    />
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </Card>

      <Card
        title="Methodology & Indicator Notes"
        description="How we measure and report impact."
      >
        <Accordion items={reportsData.methodology_notes ?? []} />
      </Card>

      <Card
        title="Verified Success Stories"
        description="Impact case studies from the portfolio."
      >
        <div className="funderReports-stories">
          {(reportsData.verified_success_stories ?? []).map((story) => (
            <article key={story.id} className="funderReports-story">
              <div className="funderReports-story-body">
                <header className="funderReports-story-header">
                  <h3 className="funderReports-story-title">{story.title}</h3>
                  {story.verified && (
                    <span className="funderReports-verified">Verified</span>
                  )}
                </header>
                <p className="funderReports-story-venture">{story.venture}</p>
                <p className="funderReports-story-summary">
                  {story.description}
                </p>
                <p className="funderReports-story-impact">
                  Impact: {story.impact}
                </p>
              </div>
              <a
                href={story.link}
                target="_blank"
                rel="noreferrer"
                className="funderReports-iconButton"
                aria-label={`Open ${story.title}`}
              >
                <ExternalLink
                  className="funderReports-icon-sm"
                  aria-hidden="true"
                />
              </a>
            </article>
          ))}
        </div>
      </Card>
    </div>
  );
}

// sub component: Download button with format prop (PDF, Excel, etc.)

function DownloadButton({ format, link }) {
  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      className="funderReports-downloadBtn"
    >
      <Download className="funderReports-icon-sm" aria-hidden="true" />
      <span>{format}</span>
    </a>
  );
}

// Native details/summary accordion. `items`: [{ title, body }].
function Accordion({ items }) {
  return (
    <div className="funderReports-accordion">
      {items.map((item) => (
        <details key={item.id ?? item.title} className="funderReports-acc-item">
          <summary className="funderReports-acc-trigger">
            <span>{item.title}</span>
            <ChevronDown
              className="funderReports-acc-chevron"
              aria-hidden="true"
            />
          </summary>
          <div className="funderReports-acc-body">{item.description}</div>
        </details>
      ))}
    </div>
  );
}
